import React, { FC } from 'react';
import styled from '@emotion/styled';
import { ReactComponent as EligibleIllustration } from '../assets/eligible.svg';
import { ReactComponent as NotEligibleIllustration } from '../assets/marginalia-waiting.svg';
import { BodyTextMedium, HeadingText50, pxToRem } from '@modernhealth/vital-components';
import { EligibilityStatus } from 'src/modules/dependents/pages/InviteDependentPage';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';

interface EligibilityInfoProps {
    status: EligibilityStatus;
}

const headerTitleDescription: Record<
    EligibilityInfoProps['status'],
    { title: string; description: string }
> = {
    notEligible: {
        title: 'dependent_eligibility.not_eligible.title',
        description: 'dependent_eligibility.not_eligible.description',
    },
    therapy: {
        title: 'dependent_eligibility.eligible_therapy.title',
        description: 'dependent_eligibility.eligible_therapy.description',
    },
    join: {
        title: 'dependent_eligibility.eligible_join.title',
        description: 'dependent_eligibility.eligible_join.description',
    },
};

export const EligibilityInfo: FC<EligibilityInfoProps> = ({ status }) => {
    const { t } = useTranslation();
    const { title, description } = headerTitleDescription[status];
    const { coveredTherapyVisits, coveredCoachingVisits } = useSelector(getSessionUtilizationInfo);

    const getImage = () => {
        if (status === 'notEligible') {
            // SVG itself has a bottom padding
            return <NotEligibleIllustration />;
        }

        return <EligibleIllustration style={{ marginBottom: pxToRem(30) }} />;
    };

    return (
        <Container>
            {getImage()}
            <HeadingText50 marginBottom={20} centered>
                {t(`dependents:${title}`)}
            </HeadingText50>
            <BodyTextMedium centered>
                {t(`dependents:${description}`, {
                    coachingCount: coveredCoachingVisits,
                    therapyCount: coveredTherapyVisits,
                })}
            </BodyTextMedium>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});
